"use client";

import api from "@/lib/axiosInstance";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Facebook, Instagram, MessageCircle, ArrowRight, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Link from "next/link";

const contactSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z
    .string()
    .regex(/^[\d\s+\-()]+$/, "Invalid phone number")
    .min(10, "Phone number must be at least 10 digits"),
  subject: z.string().min(1, "Please select a subject"),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const contactDetails = [
  {
    icon: Mail,
    label: "Email us",
    value: "admin@renpowerltd.com",
    href: "mailto:admin@renpowerltd.com",
    color: "bg-[#17BC2E]/10 text-[#17BC2E]",
  },
  {
    icon: Phone,
    label: "Call us",
    value: "+234 816 124 3325",
    href: "tel:+2348161243325",
    color: "bg-blue-50 text-blue-600",
  },
  {
    icon: MapPin,
    label: "Visit us",
    value: "Lagos, Nigeria",
    href: "#",
    color: "bg-orange-50 text-orange-500",
  },
  {
    icon: Clock,
    label: "Working hours",
    value: "Mon - Fri, 9am - 6pm WAT",
    href: "#",
    color: "bg-purple-50 text-purple-500",
  },
];

export default function ContactPage() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    mode: "onBlur",
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      const response = await api.post("/contact", data);
      toast.success(response.data.msg || "Message sent successfully!");
      reset();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.msg || "Failed to send message. Please try again.";
      toast.error(errorMsg);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f6f6]">

      {/* Hero header */}
      <section className="bg-[#002301] pt-28 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" width="30" height="30" patternUnits="userSpaceOnUse">
                <circle cx="1.5" cy="1.5" r="1.5" fill="#17BC2E" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
        </div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#17BC2E]/10 rounded-full blur-3xl pointer-events-none" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 max-w-6xl mx-auto"
        >
          <p className="text-[#17BC2E] text-sm font-semibold uppercase tracking-widest mb-3">Get in touch</p>
          <h1 className="text-white text-4xl sm:text-5xl font-bold leading-tight mb-4">
            We're here to help you <br className="hidden sm:block" />
            <span className="text-[#FFB600]">go solar.</span>
          </h1>
          <p className="text-white/60 text-base max-w-xl leading-relaxed">
            Got questions about solar products, finding a verified provider, or joining our network? Reach us via any channel below and we'll respond within 24 hours.
          </p>
        </motion.div>
      </section>

      {/* Main content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">

          {/* Left: contact info */}
          <div className="lg:col-span-2 space-y-5">
            {/* Contact detail cards */}
            {contactDetails.map(({ icon: Icon, label, value, href, color }, i) => (
              <motion.a
                key={label}
                href={href}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="flex items-center gap-4 bg-white border border-gray-100 rounded-2xl p-4 shadow-sm hover:shadow-md hover:border-[#17BC2E]/30 transition-all duration-300 group"
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{label}</p>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5 group-hover:text-[#002301] transition-colors">{value}</p>
                </div>
              </motion.a>
            ))}

            {/* Social links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="bg-[#002301] rounded-2xl p-6"
            >
              <p className="text-white font-semibold mb-1">Follow us on social media</p>
              <p className="text-white/50 text-xs mb-4">Stay updated with solar news and platform announcements.</p>
              <div className="flex items-center gap-3">
                <a
                  href="https://web.facebook.com/profile.php?id=61553076221891"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#FFB600] flex items-center justify-center transition-colors duration-200 group"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4 text-white group-hover:text-black" />
                </a>
                <a
                  href="https://www.instagram.com/renpowerltd/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#FFB600] flex items-center justify-center transition-colors duration-200 group"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4 text-white group-hover:text-black" />
                </a>
                <a
                  href="https://wa.me/2348161243325"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#25D366] flex items-center justify-center transition-colors duration-200 group"
                  aria-label="WhatsApp"
                >
                  <MessageCircle className="w-4 h-4 text-white" />
                </a>
              </div>

              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="text-white/50 text-xs mb-2">Prefer to chat directly?</p>
                <a
                  href="https://wa.me/2348161243325"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-[#1ebe5d] transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Chat on WhatsApp
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-8"
          >
            <h2 className="text-2xl font-bold text-[#002301] mb-1">Send us a message</h2>
            <p className="text-gray-400 text-sm mb-6">Fill in the form and our team will get back to you within 24 hours.</p>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fullName" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("fullName")}
                    type="text"
                    id="fullName"
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#17BC2E]/30 focus:border-[#17BC2E] disabled:bg-gray-50 transition outline-none"
                    placeholder="Chidi Okafor"
                  />
                  {errors.fullName && <p className="mt-1 text-xs text-red-500">{errors.fullName.message}</p>}
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("email")}
                    type="email"
                    id="email"
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#17BC2E]/30 focus:border-[#17BC2E] disabled:bg-gray-50 transition outline-none"
                    placeholder="you@example.com"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register("phone")}
                    type="tel"
                    id="phone"
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#17BC2E]/30 focus:border-[#17BC2E] disabled:bg-gray-50 transition outline-none"
                    placeholder="+234 801 234 5678"
                  />
                  {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
                </div>
                <div>
                  <label htmlFor="subject" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register("subject")}
                    id="subject"
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#17BC2E]/30 focus:border-[#17BC2E] disabled:bg-gray-50 transition outline-none bg-white"
                  >
                    <option value="">Select a subject</option>
                    <option value="solar-products">Solar Products</option>
                    <option value="partnership">Partnership</option>
                    <option value="recycling">Recycling Program</option>
                    <option value="support">Support</option>
                    <option value="others">Others</option>
                  </select>
                  {errors.subject && <p className="mt-1 text-xs text-red-500">{errors.subject.message}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register("message")}
                  id="message"
                  rows={5}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#17BC2E]/30 focus:border-[#17BC2E] resize-none disabled:bg-gray-50 transition outline-none"
                  placeholder="Tell us how we can help you with solar energy..."
                />
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#002301] text-white py-3.5 rounded-xl font-semibold text-sm hover:bg-black transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                By submitting, you agree to our{" "}
                <Link href="/privacy-policy" className="underline hover:text-[#002301]">Privacy Policy</Link>.
              </p>
            </form>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
